/**
 * Animated sidebar toggle icon with 4 states using Framer Motion.
 *
 * Inspired by Linear's sidebar toggle - features:
 * - Rounded rectangle container
 * - Vertical divider line
 * - Small horizontal lines representing sidebar menu items
 * - Proper arrow (horizontal stem + chevron head) on hover
 */

import * as stylex from "@stylexjs/stylex";
import { motion } from "framer-motion";

import { useAnimation } from "@animus/primaris";
import { colors } from "@animus/primaris/tokens.stylex";

const styles = stylex.create({
  svg: {
    width: 20,
    height: 20,
    display: "block",
    color: colors.foregroundSubtle,
  },
});

const fadeConfig = {
  duration: 0.15,
};

export interface SidebarToggleIconProps {
  isCollapsed: boolean;
  isDenied?: boolean;
  isHovered?: boolean;
}

const shakeKeyframes = [0, -12, 12, -10, 10, -7, 7, -4, 4, -2, 2, 0];

export function SidebarToggleIcon({
  isCollapsed,
  isDenied = false,
  isHovered = false,
}: SidebarToggleIconProps) {
  const { getSpring } = useAnimation();
  const quickSpring = getSpring("quick");
  const showArrow = isCollapsed || isHovered;

  const getArrowOpacity = () => {
    if (isCollapsed) {
      return isHovered ? 1 : 0.4;
    }
    return isHovered ? 1 : 0;
  };

  const getArrowX = () => {
    if (isCollapsed) {
      return isHovered ? 0 : -2;
    }
    return isHovered ? 0 : 2;
  };

  const getSidebarContentX = () => {
    if (!isCollapsed) {
      return isHovered ? -2 : 0;
    }
    return isHovered ? 0 : -1.5;
  };
  const getSidebarContentOpacity = () => {
    if (!isCollapsed) {
      return 1;
    }
    return isHovered ? 1 : 0.4;
  };

  return (
    <motion.svg
      {...stylex.props(styles.svg)}
      animate={{ rotate: isDenied ? shakeKeyframes : 0 }}
      aria-hidden="true"
      fill="none"
      initial={false}
      transition={isDenied ? { duration: 0.5, ease: "easeOut" } : quickSpring}
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        fill="none"
        height={13}
        rx={2.5}
        stroke="currentColor"
        strokeWidth={1.2}
        width={13}
        x={2.5}
        y={2.5}
      />

      <motion.g
        animate={{
          x: getSidebarContentX(),
          opacity: getSidebarContentOpacity(),
        }}
        initial={false}
        transition={quickSpring}
      >
        <line
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth={1.2}
          x1={7.5}
          x2={7.5}
          y1={4.5}
          y2={13.5}
        />

        <line
          opacity={0.6}
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth={1.2}
          x1={4}
          x2={6}
          y1={6}
          y2={6}
        />
        <line
          opacity={0.6}
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth={1.2}
          x1={4}
          x2={6}
          y1={8.5}
          y2={8.5}
        />
      </motion.g>

      <motion.g
        animate={{
          opacity: getArrowOpacity(),
          x: getArrowX(),
        }}
        initial={false}
        transition={quickSpring}
      >
        {isCollapsed ? (
          <>
            <motion.line
              animate={{ pathLength: showArrow ? 1 : 0 }}
              initial={false}
              stroke="currentColor"
              strokeLinecap="round"
              strokeWidth={1.2}
              transition={fadeConfig}
              x1={10}
              x2={12.5}
              y1={9}
              y2={9}
            />
            <path
              d="M12 7.5 L14 9 L12 10.5"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.2}
            />
          </>
        ) : (
          <>
            <motion.line
              animate={{ pathLength: showArrow ? 1 : 0 }}
              initial={false}
              stroke="currentColor"
              strokeLinecap="round"
              strokeWidth={1.2}
              transition={fadeConfig}
              x1={12.5}
              x2={10}
              y1={9}
              y2={9}
            />
            <path
              d="M10.5 7.5 L8.5 9 L10.5 10.5"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.2}
            />
          </>
        )}
      </motion.g>
    </motion.svg>
  );
}
